<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-performance-overview-page" v-loading="loading" element-loading-text="Loading...">
    <div class="performance-overview-title-small">{{$t(currentPageInfo.subtitle)}}</div>
    <div class="button-flex">
      <div class="performance-overview-title">{{$t(currentPageInfo.title)}}</div>
      <wy-page-tooltip class="tooltip"/>
    </div>
    <div class="button-flex">
        <button class="filter-button" :class="{'selected': selection=='all'}" @click="handlePerformanceSelection('all')">All intents</button>
        <button class="filter-button" :class="{'selected': selection=='contained'}" @click="handlePerformanceSelection('contained')">Intents to contain</button>
        <button class="filter-button" :class="{'selected': selection=='handoff'}" @click="handlePerformanceSelection('handoff')" >Intents to handoff</button>
    </div>
    <div ref="panelsContainer" class="components-container"  v-show="!loading" >
      <keep-alive v-for="(panel, index) in activePanels" :key="index">
        <component :ref="panel.key" :id="panel.key" class="panel-component-section" :is="panel.component" :controller="module" :panelTitle="panel.name" v-bind="panel.properties"></component>
      </keep-alive>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import WyPerformanceOverviewPanelContainedConversations from './panel-templates/wy-performance-overview-panel-contained-conversations'
import WyPerformanceOverviewPanelHandedOffConversations from './panel-templates/wy-performance-overview-panel-handed-off-conversations'
import WyPerformanceOverviewPanelMessageUnderstanding from './panel-templates/wy-performance-overview-panel-message-understanding'
import WyPerformaceOverviewPanelConversationsByLanguages from './panel-templates/wy-performance-overview-panel-conversations-by-languages'
import WyPerformaceOverviewPanelConversationsByChannels from './panel-templates/wy-performance-overview-panel-conversations-by-channels'
import WyPerformanceOverviewPanelTopIntents from './panel-templates/wy-performance-overview-panel-top-intents.vue'
import WyPageTooltip from '../widgets/wy-page-tooltip.vue';
import Sortable from 'sortablejs';

export default Vue.extend({
  name: 'wy-performance-overview-page',
  components: { 
    'wy-performance-overview-panel-contained-conversations': WyPerformanceOverviewPanelContainedConversations,
    'wy-performance-overview-panel-handed-off-conversations': WyPerformanceOverviewPanelHandedOffConversations,
    'wy-performance-overview-panel-message-understanding': WyPerformanceOverviewPanelMessageUnderstanding,
    'wy-performance-overview-panel-conversations-by-languages': WyPerformaceOverviewPanelConversationsByLanguages,
    'wy-performance-overview-panel-conversations-by-channels': WyPerformaceOverviewPanelConversationsByChannels,
    'wy-performance-overview-panel-top-intents': WyPerformanceOverviewPanelTopIntents,
    'wy-page-tooltip': WyPageTooltip,
  },

  data() {
    return {
      module: this,
      pagePanels: [
        "wy-performance-overview-panel-contained-conversations",
        "wy-performance-overview-panel-handed-off-conversations",
        "wy-performance-overview-panel-message-understanding",
        "wy-performance-overview-panel-conversations-by-channels",
        "wy-performance-overview-panel-conversations-by-languages",
        "wy-performance-overview-panel-top-intents"
      ],
      loading: false,
      rawData: {},
      overviewData: {},
      selection: 'all',
      initDate: null,
    }
  },

  watch: {
  },

  computed: {
    ...mapGetters(['dataModel', 'isLoggedIn', 'config', 'companyConfig', 'currentPagePanels', 'currentPageName', 'currentPageInfo']),

    activePanels() {
      const pagePanelComponents = [];
      for(const pagePanel of this.pagePanels) {
        const panel = this.config.appPanels.find(el => el.key == pagePanel);
        if(panel && panel.enabled) {          
          pagePanelComponents.push({
            key: panel.key,
            name: panel.name,
            properties: panel.properties,
            component: panel.key
          })
        }
      }
      return pagePanelComponents;
		}
  },

  methods: {
    ...mapActions(['preparePerformanceViewByData', 'getPagePanels', 'updatePagePanels']),

    handlePerformanceSelection(selection) {
      this.$store.dispatch('amplitudeLogWithFilters', {name: 'performance_overview_intents_selection', data: {intents_selection: selection}});
      let isAllReady = true;
      this.$children.map(component => {
        if(component.handleFilterChange) {
          if(component.loading != false) {
            isAllReady = false;
          }
        }
      })
      if(isAllReady) {
        this.selection = selection;
        this.handleFilterChange();
      }
    },

    async changeDateRange(dateRange, rawData, templateData, view) {
      // this.dateRange = dateRange;
      view.pannelData = await this.preparePerformanceViewByData({viewBy: dateRange, rawData: rawData, templateData: templateData, companyConfig: this.companyConfig})
      view.sectionData = view.pannelData.data[view.dataIndex];
      view.renderLegendData();
      view.$refs.tabSection.$refs.tabBottomSection.renderLegends();
      this.refreshOverviewPanel(view, view.sectionData);
    },

    handleFilterChange() {
      this.$children.map(component => {
        if(component.handleFilterChange) {
          component.handleFilterChange();
        }
      })
    },

    refreshOverviewPanel(view, data) {
      if(view) {
        //prepare view by data
        view.pannelData.data.forEach((sectionData)=>{
          if(sectionData.key == view.sectionData.key) {
            sectionData.selected = true
          }
        })

        if(view.$refs.tabSection.$refs.lineChart || view.$refs.tabSection.$refs.pieChart) {
          if(data.lineChartData) view.$refs.tabSection.$refs.lineChart.buildChart();
          if(data.pieChartData) view.$refs.tabSection.$refs.pieChart.buildChart();
          if(data.columnChartData) view.$refs.tabSection.$refs.columnChart.buildChart();
        }
      }
    },
    setDefaultDateRange() {
      if(this.dataModel.dateRangeInDays < 7){
        return  'day';
      } else if(this.dataModel.dateRangeInDays < 30){
        // return 'week';
        return  'day';
      } else if(this.dataModel.dateRangeInDays < 365){
        return 'week';
        // return 'month'
      } else{
        return 'week';
        // return 'year'
      }
    }
  },

  mounted() {
    this.$root.registerTooltips(this.$el);
    
    Sortable.create(this.$refs.panelsContainer, { 
      handle: '.move-info',
      onUpdate: (evt) => {
        let sortedPanels = [];
        for(const el of evt.to.childNodes) {
          sortedPanels.push(el.id);
        }
        this.updatePagePanels(sortedPanels);
      }
    });
  },

  async created() {
    this.$router.currPageComponent = this;
    this.initDate = new Date();
    // listen events from other components
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if(this[event.name]) this[event.name]();
    });

    this.pagePanels = this.currentPagePanels(this.pagePanels);
  },

  destroyed() {
    this.$root.unregisterTooltips(this.$el);
    if(this.isLoggedIn) {
      this.$store.dispatch('amplitudeLogWithFilters', {name: 'performance_page_time_spent', data: {time_spent: WyUtils.getTimeDiff(this.initDate)}});
    }
  }
});
</script>

<style lang="scss">
.overview-page-button-poppers {
  font-family: Helvetica Neue, Helvetica;
  font-size: 14.22px;
  font-weight: 400;
}
.icon-info{
  display: inline-block;
  background: url('../../assets/icons/icon_info_performance_overview.svg') no-repeat;
  width: 2.5vw;
  height: 2.1vw;
  position: relative;
  top: 0.9vw;
  margin-left: 0.25vw;
}
.wy-performance-overview-page {
  // min-width: 1123px;
  // min-height: calc(100% - 20px);
  min-height: calc(100% - 30px);
  padding-right: 0px;
  font-weight: 500;
  font-size: 16px;
  padding-top: 0px;
  overflow-x: hidden;
  overflow-y: hidden;

  
  .filter-button {
    border-radius: 20px;
    padding: 10px 20px;
    color: var(--site-font-secondary-color);
    font-size: 16px;
    font-weight: 500;
    font-family: Helvetica Neue, Helvetica;
    background-color: #FFF;
    margin-right: 0.5vw;
    border: 1px solid #DCDFE6;
  }
  .filter-button.selected {
    background: #0C2837 !important;
    border-color: #DCDFE6 !important;
    color: #FCFEFF !important;
  }

  .filter-button:focus, .filter-button:hover {
    background: #F4F6F8;
    border-color: #CAD2DA;
    color: #0C2837;
  }
  .button-flex{
    display: flex;
  }
  .tooltip {
    padding-top: 4px;
    padding-left: 8px;
  }
  .performance-overview-title-small{
    color: var(--site-font-secondary-color);
    font-size: 15px;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 400;
  }
  .performance-overview-title{
    color: var(--site-font-secondary-color);
    font-size: 25px;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 600;
    margin-bottom: 25px;
  }

// global panel container layout
  .panel-component-section.loading-height {
    height: 695px;
  }
  .panel-component-section {
    min-height: calc(100% - 20px);
    padding-right: 0px;
    margin-bottom: -20px;
    background: #FFF;
    border: 1px solid #CAD2DA;
    width: 100%;
    height:auto;
    // height:695px;
    border-radius: 5px;
    margin-top: 20px;
    margin-right: 20px;

    .el-loading-mask {
      height: 588px !important;
      top: 85px;
    }
    .full-size .el-loading-mask {
      top: 85px;
      height: calc(100% - 64px) !important;
    }
    .bottom-right-flex {
      .chart-header {
        display: flex;
        .compare-group-by {
          margin-right: 20px;
          margin-left: auto;
        }
      }
    }
  }
  .components-container{
    width: 99.5%;
    margin-top: 5px;
    margin-bottom: 50px;
    display: grid;
    column-gap: 25px;
    row-gap: 25px;
    grid-template-columns: 1fr 1fr;
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: 50%;
    grid-template-areas: 'item item';
    

    .wy-performance-overview-row > div:last-child {
      margin-right: 0px;
    }
  }
  
}

@media screen  and (min-width: 2000px) {
  .wy-performance-overview-page {
    .components-container .panel-component-section {

      .panel-title {
        font-size: 0.9vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 0.9vw;
      }
      .value-previous-days{
        line-height: 1.6vw;
        font-size: 0.9vw;
        height: 1.6vw;
        padding: 0.2vw 0.9vw 0.2vw 1.4vw
      }
      .arrow-up {
        background-position-y: 0.58vw;
        background-position-x: 12.6px;
        background-size: 0.68vw;
      }
      .arrow-down {
        background-position-y: 0.58vw;
        background-position-x: 12.6px;
        background-size: 0.68vw;
      }
      .value-start-period{
        line-height: 1.6vw;
        font-size: 0.9vw;
        height: 1.6vw;
        padding: 0.2vw 0.9vw 0.2vw 1.4vw
      }
      .change-over-time-texts{
        font-size: 0.66vw;
      }
      .bottom-flex-title{
        font-size: 16px;
      }
      .panel-top-flex{
        // border-right: 1px #CAD2DA solid;
        // width: 337px;
        width: 100%;
        height: 120px
      }
      .panel-top--title {
        font-size: 14.5px;
      }
      .panel-body-flex{
        width: 100%;
        // height: 300px;
        display: flex;
        // flex-direction: column;
        .div-pie-section {
          width: 40%;
        }
        .div-column-section {
          width: 60%;
        }
      }
      .panel-bottom-flex {
        .chart-legend {
          width: 100%;
          height: 50px;
        }
      }
      .panel-component-section {
        min-height: calc(100% - 20px);
        padding-right: 0px;
        // margin-bottom: 10px;
        border: 1px solid #CAD2DA;
        width: 50%;
        height: 100%;
        border-radius: 5px;
        margin-top: 20px;
        margin-right: 20px;
        .panel-top-flex{
          // border-right: 1px #CAD2DA solid;
          // width: 337px;
          width: 100%;
          height: 100px
        }
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px;
          width: calc(100% - 14px);
          .stats, .stats-title {  
            padding-left: 2px;
          }
        }
      }
      
      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              // padding-left: 25px;
              // padding-left: 16px;
              width: 100%;

              .stats {
                font-size: 1.6vw;
              }
              .stats-title {
                font-size: 0.917vw;
                margin-bottom: 0.3vw;
              }
            }
            .tab-section.selected {
              border-bottom: 0.2vw #000 solid;
            }
          }
        }
      }
    }
  }
}

@media screen and (min-width: 1085px) and (max-width: 1400px) {
  .wy-performance-overview-page .components-container .wy-performance-overview-panel-top-intents.panel-component-section {
    height: 693px !important;
  }
  .components-container{
    grid-template-columns: 100% !important;
    grid-template-areas: unset !important;
  }
  .wy-performance-overview-page {
    .performance-overview-title-small{
      font-size: 1.3vw;
    }
    .performance-overview-title{
      // margin-bottom: 0.8vw;
      font-size: 2.3vw;
    }
    .filter-button{
      font-size: 1.3vw;
    }
    // .components-container{
    //   grid-template-columns: unset!important;
    // }
    .components-container .panel-component-section{
      display: block;

      .panel-title {
        font-size: 1.5vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 1.2vw;
      }
      .panel-component-section {
        width: calc(100% - 2px);
        height: 100%;
      }

      .value-previous-days{
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 2vw;
        padding-right: 10px;
        line-height: 2.9vw;
        height: 2.8vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .value-start-period{
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 2vw;
        padding-right: 10px;
        line-height: 2.9vw;
        height: 2.8vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .arrow-up {
        background-position-y: 0.8vw;
        background-position-x: 8px;
        background-size: 1.08vw;
      }
      .arrow-down {
        background-position-y: 0.8vw;
        background-position-x: 8px;
        background-size: 1.08vw;
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px !important;
          width: calc(100% - 14px) !important;
        }
      }
      .overview-table td .tab-section {
        width: 100% !important;
      }
      .panel-top--title {
        font-size: 14.5px;
      }


      .change-over-time-texts{
        width: 55%; 
        height: 36px;
        font-size: 1.2vw;
        font-family: Helvetica Neue, Helvetica;
        color: var(--site-font-tertiary-color);
        font-weight: normal;
        padding: 0 10px 0 10px;
      }
      .bottom-flex-title{
        padding: 15px;
        font-size: 1.2vw;
        font-weight: medium;
        color: var(--site-font-secondary-color);
      }
      .wy-panel-top-section .overview-table td .tab-section.selected {
        border-bottom: 0.3vw #0C283F solid !important;
      }
      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          border-bottom: 1px #CAD2DA solid;
          width: 100%;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              padding: 18px 14px;
              padding-right: 0px !important;
              cursor: pointer;

              &:hover{
                background: #F4F6F8;
              }

              .stats {
                .section-value {
                  font-size: 2.4vw;
                  color: var(--site-font-secondary-color);
                }
                margin-right: 5px;
              }
              .stats-title {
                font-size: 1.4vw;
                color: var(--site-font-tertiary-color);
              }
              svg.ct-chart-line {
                width: 100% !important;
              }
            }
          }
        }
      }

      .panel-bottom-flex {
        height: 105px;
      }
    }
  }

}
@media screen and (max-width: 1085px) {

  .wy-performance-overview-page .components-container .wy-performance-overview-panel-top-intents.panel-component-section {
    height: 693px !important;
  }
  .components-container{
    grid-template-columns: 100% !important;
    grid-template-areas: unset !important;
    min-width: 500px;
  }
  .wy-performance-overview-page {
    .performance-overview-title-small{
      font-size: 1.3vw;
    }
    .performance-overview-title{
      // margin-bottom: 0.8vw;
      font-size: 2.3vw;
    }
    .filter-button{
      font-size: 1.3vw;
    }
    // .components-container{
    //   grid-template-columns: unset!important;
    // }
    .components-container .panel-component-section{
      display: block;

      .panel-title {
        font-size: 1.5vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 1.2vw;
      }
      .panel-component-section {
        width: calc(100% - 2px);
        height: 100%;
      }

      .value-previous-days{
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 2.1vw;
        padding-right: 9px;
        line-height: 2.9vw;
        height: 2.8vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .value-start-period{
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 2.1vw;
        padding-right: 9px;
        line-height: 2.9vw;
        height: 2.8vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .arrow-up {
        background-position-y: 0.84vw;
        background-position-x: 6px;
        background-size: 1.07vw;
      }
      .arrow-down {
        background-position-y: 0.84vw;
        background-position-x: 6px;
        background-size: 1.07vw;
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px !important;
          width: calc(100% - 14px)!important;
        }
      }
      .overview-table td .tab-section {
        width: 100% !important;
      }
      .panel-top--title {
        font-size: 14.5px;
      }


      .change-over-time-texts{
        width: 55%;
        height: 36px;
        font-size: 1.2vw;
        font-family: Helvetica Neue, Helvetica;
        color: var(--site-font-tertiary-color);
        font-weight: normal;
        padding: 0 10px 0 10px;
      }
      .bottom-flex-title{
        padding: 15px;
        font-size: 1.2vw;
        font-weight: medium;
        color: var(--site-font-secondary-color);
      }
      .wy-panel-top-section .overview-table td .tab-section.selected {
        border-bottom: 0.3vw #0C283F solid !important;
      }
      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          border-bottom: 1px #CAD2DA solid;
          width: 100%;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              padding: 18px 14px;
              padding-right: 0px !important;
              cursor: pointer;

              &:hover{
                background: #F4F6F8;
              }

              .stats {
                .section-value {
                  font-size: 2.4vw;
                  color: var(--site-font-secondary-color);
                }
                margin-right: 5px;
              }
              .stats-title {
                font-size: 1.4vw;
                color: var(--site-font-tertiary-color);
              }
              svg.ct-chart-line {
                width: 100% !important;
              }
            }
          }
        }
      }

      .panel-bottom-flex {
        height: 105px;
      }
    }
  }

}
</style>